import React, { useState } from "react";
import { useTranslation } from "@/i18n/LanguageContext";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

interface SolveDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (privateKey: string) => void;
  isSubmitting: boolean;
}

export function SolveDialog({ isOpen, onClose, onSubmit, isSubmitting }: SolveDialogProps) {
  const { t } = useTranslation();
  const [privateKey, setPrivateKey] = useState("");
  const [privateKeyError, setPrivateKeyError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset any previous error
    setPrivateKeyError(null);
    
    // Validate private key format (64 character hex string)
    const hexRegex = /^[0-9a-fA-F]{64}$/;
    if (!hexRegex.test(privateKey)) {
      setPrivateKeyError(t("desafios_cacatais.solve_dialog.invalid_format", { 
        fallback: "Private key must be a 64-character hexadecimal string." 
      }));
      return;
    }
    
    onSubmit(privateKey);
  };

  const handleDialogChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-[95vw] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("desafios_cacatais.solve_dialog.title")}</DialogTitle>
          <DialogDescription>
            {t("desafios_cacatais.solve_dialog.description")}
            <div className="mt-2 text-yellow-400 font-semibold">
              {t("desafios_cacatais.solve_dialog.cost_warning", { fallback: "Each try costs 1 cacatal." })}
            </div>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="privateKey">{t("desafios_cacatais.solve_dialog.private_key")}</Label>
              <Input
                id="privateKey"
                value={privateKey}
                onChange={(e) => {
                  setPrivateKey(e.target.value);
                  setPrivateKeyError(null);
                }}
                placeholder={t("desafios_cacatais.solve_dialog.private_key_placeholder")}
                className="bg-zinc-800 border-zinc-700"
                required
              />
            </div>
            {privateKeyError && (
              <div className="text-red-500 text-sm">{privateKeyError}</div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
              {t("desafios_cacatais.solve_dialog.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("desafios_cacatais.solve_dialog.checking")}
                </>
              ) : (
                t("desafios_cacatais.solve_dialog.submit")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
